/**
 * @ken/module-jobs — frontend entry point.
 *
 * Jobs module per ADR-0155 (any company that enables it; SS-B6-T1 G35
 * removed its business-type restriction). Exports the createRoutes
 * factory the cockpit shell expects per KEN_LAYER_7 §6.3. The manifest is
 * not exported here (CV-19) — modules/jobs/manifest.yaml is the only copy,
 * and the build reads it directly.
 *
 * The cockpit's composeModuleRoutes wraps every registered module route
 * in <ModuleGate slug={name}> automatically, so a company whose
 * enabled_modules array doesn't include "jobs" will get a route-level 403 if they try to deep-link
 * to /jobs without the manifest installed for them.
 *
 * 2026-06-15 (story_page_go_wide): the in-list detail Sheet is RETIRED for
 * jobs in favour of the full-page STORY route (ADR-0081/0082, PARTY
 * archetype). Clicking a job row navigates to `jobs/$id/overview`; the parent
 * `jobs/$id` renders the StoryPageDetail shell + routed tab children (overview
 * / related / invoices / notes) with an index redirect to overview. Mirrors
 * the customers party adoption exactly.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { JobStorySearch } from "./routes/story/job-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadJobStoryTabs = () => import("./routes/story/job-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const JobListPage = lazyScreen(
  () => import("./routes/job-list"),
  "JobListPage",
);
const JobStoryDetail = lazyScreen(
  () => import("./routes/story/job-story-detail"),
  "JobStoryDetail",
);
const JobActivityRoute = lazyScreen(
  loadJobStoryTabs,
  "JobActivityRoute",
);
const JobInvoicesRoute = lazyScreen(
  loadJobStoryTabs,
  "JobInvoicesRoute",
);
const JobNotesRoute = lazyScreen(
  loadJobStoryTabs,
  "JobNotesRoute",
);
const JobOverviewRoute = lazyScreen(
  loadJobStoryTabs,
  "JobOverviewRoute",
);
const JobRelatedRoute = lazyScreen(
  loadJobStoryTabs,
  "JobRelatedRoute",
);
export type {
  JobListItem,
  JobListResponse,
  JobDetailResponse,
  JobLinkedInvoice,
  FieldDefinition,
  FieldDefinitionsResponse,
} from "./api/client";

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "jobs",
    // The list's parent filter + the originating row highlight are URL-borne
    // so back-navigation from the story page restores them (ADR-0081 §3).
    // `view` (grouped|flat) is URL-borne too so the toggle survives reload +
    // back, consistent with how filter/search ride the router search params.
    validateSearch: (raw: Record<string, unknown>): JobListSearch => ({
      ...(typeof raw["parent"] === "string" && raw["parent"]
        ? { parent: raw["parent"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      ...(raw["view"] === "flat" ? { view: "flat" } : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: JobListPage,
  });

  // ── Story-page detail (ADR-0081): parent jobs/$id shell + routed tab
  //    children + an index redirect to …/overview. ────────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "jobs/$id",
    // The list's parent filter + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): JobStorySearch => ({
      ...(typeof raw["parent"] === "string" && raw["parent"]
        ? { parent: raw["parent"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: JobStoryDetail,
  });

  // Index redirect jobs/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/jobs/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: JobOverviewRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: JobRelatedRoute,
  });
  const invoicesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "invoices",
    component: JobInvoicesRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: JobNotesRoute,
  });
  // UI-05: the Activity tab has been in the tab strip since the story page
  // shipped, with no child route behind it — the tab was offered and led
  // nowhere. Registered here, as a real child route rather than a special
  // case inside the page, so UI-04's declared-tabs check resolves it.
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: JobActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      relatedRoute,
      invoicesRoute,
      notesRoute,
      activityRoute,
    ],
  );

  return [listRoute, detailRoute];
};

/** List search params (parent filter + highlight + view) — URL-borne for
 *  restore. `view` is "flat" for the flat table; absent (the default) is the
 *  grouped tree. */
export interface JobListSearch {
  parent?: string;
  highlight?: string;
  view?: "flat";
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
